import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { English } from "../../constant/EnglishFallback";
import { BIBlue } from "../../assets/helpers";
import {
  Avatar,
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Grid,
  Skeleton,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import InfiniteScroll from "react-infinite-scroll-component";
import Emptydata from "../common/Emptydata";
import { Message } from "@mui/icons-material";
import Post from "./Post";
import { showAddPostModal, setCommunityPostIds, setShowReport } from "../../redux/actions/actions";
import { AddPostDialog } from "../common/AddPostDialog";
import { AddButton } from "../common/AddButton";
import postTrackingManager from "./PostTracking";
import { useAdsManager } from "./useAdsManager";
import { Ads } from "../../common/Ads";
import UserSuggestion from "./UserSuggestion";
import { Postrequestcall } from "../../request/Postrequest";
import { FETCH_POST_IDS, GET_POSTS_BY_ID, GET_SITES_POSTS_BY_ID } from "../../constant/Apipath";
import Pages from "../../constant/ReportModel";
import { useLocation } from "react-router-dom";
import PostComment from "./PostComment";

const PAGE_SIZE = 14;

export default function Community() {
  const {
    loginData,
    showAddpost,
    multiLanguageData,
    communityPostIds,
    reportSuccess,
    reportData,
    selectedLanguage
  } = useSelector((state) => state.main);

  const dispatch = useDispatch();
  const location = useLocation();

  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);
  const [currentBatchIndex, setCurrentBatchIndex] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const textFieldRef = useRef(null);
  const observerRef = useRef(null);
  const postRefs = useRef({});
  const tabDebounceRef = useRef(null);
  const requestTokenRef = useRef(0);

  const [selectedTopMenu, setSelectedTopMenu] = useState(0);
  const [isTextFieldVisible, setIsTextFieldVisible] = useState(true);

  const ENGLISH = English?.Home;

  const {
    BUTTON_ALL = ENGLISH?.BUTTON_ALL,
    BUTTON_PROJECTS = ENGLISH?.BUTTON_PROJECTS,
    BUTTON_TRADES = ENGLISH?.BUTTON_TRADES,
    BUTTON_PRODUCTS = ENGLISH?.BUTTON_PRODUCTS,
    BUTTON_ADD_POST = ENGLISH?.BUTTON_ADD_POST,
    MODAL_TEXT_INFO = ENGLISH?.MODAL_TEXT_INFO,
    MODAL_ADD_POST_PLACEHOLDER = ENGLISH?.MODAL_ADD_POST_PLACEHOLDER,
    TEXT_PLACEHOLDER_NO_POSTS = ENGLISH?.TEXT_PLACEHOLDER_NO_POSTS,
    TEXT_ALL_POST_SEEN = ENGLISH?.TEXT_ALL_POST_SEEN
  } = multiLanguageData?.Home || {};

  const initTopMenu = [BUTTON_ALL, BUTTON_PROJECTS, BUTTON_TRADES, BUTTON_PRODUCTS];

  const { mergeAdsIntoFeed } = useAdsManager();

  const [openComment, setOpenComment] = useState(false);
  const [selectPost, setSelectPost] = useState(null);

  const finalFeed = useMemo(() => {
    return mergeAdsIntoFeed(posts, 5);
  }, [posts, selectedTopMenu]);

  // Initialize data on mount
  useEffect(() => {
    const initializeData = async () => {
      const UserId = loginData?.userInformation?.UserId;
      const accessToken = loginData?.accessToken;
      if (!UserId || !accessToken) return;
      try {
        // - Empty redux: LastKnownId=0, fetches all posts and groups them
        // - Has redux data: LastKnownId=first post id, fetches only new posts and merges
        await fetchPostIds();
      } catch (error) {
        console.error("Error initializing data: ", error);
      }
    };

    initializeData();
  }, [location?.state?.postRefresh]);

  // Initialize post tracking manager with user credentials
  useEffect(() => {
    const UserId = loginData?.userInformation?.UserId;
    const accessToken = loginData?.accessToken;
    if (UserId && accessToken) {
      postTrackingManager.initialize(UserId, accessToken);
    }
    // Cleanup on unmount
    return () => {
      postTrackingManager.flushNow(); // Send any remaining events
    };
  }, [loginData?.userInformation?.UserId, loginData?.accessToken]);

  // Setup IntersectionObserver for impression tracking
  useEffect(() => {
    // Only set up if we have posts
    if (posts.length === 0) return;

    // Get scroll container - ensure it exists
    const scrollContainer = document.getElementById("co_scrollList");
    if (!scrollContainer) return;

    // Create intersection observer for post impressions
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const postId = entry.target.getAttribute("data-id");
            if (postId && !isNaN(Number(postId))) {
              // Record impression when post becomes visible
              postTrackingManager.recordImpression(Number(postId));
            }
          }
        });
      },
      {
        root: scrollContainer,
        rootMargin: "0px",
        threshold: 0.5 // Post must be at least 50% visible to count as impression
      }
    );

    // Observe all post elements
    Object.values(postRefs.current).forEach((postElement) => {
      if (postElement && observerRef.current) {
        observerRef.current.observe(postElement);
      }
    });

    // Cleanup observer on unmount or when posts change
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [posts]); // Re-run when posts change

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Update visibility state based on intersection
          setIsTextFieldVisible(entry.isIntersecting);
        });
      },
      {
        root: null, // Use viewport as root
        rootMargin: "0px",
        threshold: 0.1 // Trigger when 10% of the element is visible
      }
    );
    if (textFieldRef.current) {
      observer.observe(textFieldRef.current);
    }
    // Cleanup observer on unmount
    return () => {
      if (textFieldRef.current) {
        observer.unobserve(textFieldRef.current);
      }
    };
  }, []);

  // After report the post remove it from list
  useEffect(() => {
    if (reportSuccess !== true) {
      if (reportData?.AppScreenId === Pages?.post?.id) {
        hidePostafterReport();
      }
    }
  }, [reportSuccess]);

  const hidePostafterReport = () => {
    let getPosts = posts.filter((post) => post.id !== reportData?.recordId);
    setPosts(getPosts); // Update the state to remove the post from UI
    let updateCommunitypostIds = communityPostIds?.all?.filter(
      (post) => post?.id !== reportData?.recordId
    );
    switch (reportData?.appName) {
      case "Product":
        let getProductIds = communityPostIds?.products?.filter(
          (post) => post?.id !== reportData?.recordId
        );
        dispatch(
          setCommunityPostIds({
            ...communityPostIds,
            all: updateCommunitypostIds,
            products: getProductIds
          })
        );
        break;
      case "UserSiteProject":
        let getProjectIds = communityPostIds?.projects?.filter(
          (post) => post?.id !== reportData?.recordId
        );
        dispatch(
          setCommunityPostIds({
            ...communityPostIds,
            all: updateCommunitypostIds,
            projects: getProjectIds
          })
        );
        break;
      case "Trade":
        let getTrades = communityPostIds?.trades?.filter(
          (post) => post?.id !== reportData?.recordId
        );
        dispatch(
          setCommunityPostIds({
            ...communityPostIds,
            all: updateCommunitypostIds,
            trades: getTrades
          })
        );
        break;
      default:
        dispatch(
          setCommunityPostIds({
            ...communityPostIds,
            all: updateCommunitypostIds
          })
        );
        break;
    }
    let reportObj = {
      showReport: false,
      recordId: 0,
      name: "",
      AppScreenId: 0,
      appName: ""
    };
    dispatch(setShowReport(reportObj));
  };

  // Fetch post IDs from API
  // First visit (communityPostIds.all empty): Get all post IDs (LastKnownId = 0)
  // Return visit: Get only new posts using LastKnownId = first post ID in Redux, then merge
  const fetchPostIds = async () => {
    const token = requestTokenRef.current;
    try {
      setIsLoading(true);
      const hasExisting = communityPostIds?.all?.length > 0;
      const lastKnownId = hasExisting ? (communityPostIds.all[0]?.id ?? 0) : 0;
      let payload = {
        PageNo: 1,
        PageSize: 0,
        UserId: Number(loginData?.userInformation?.UserId),
        FormatTypeId: 0,
        AppName: "All",
        LastKnownId: lastKnownId
      };
      let response = await Postrequestcall(FETCH_POST_IDS, payload, loginData?.accessToken);
      if (token !== requestTokenRef.current) return;
      if (response.status === 200) {
        const fetchedData = response?.data?.data;
        if (Array.isArray(fetchedData) && fetchedData.length > 0) {
          const grouped = {
            all: [],
            products: [],
            projects: [],
            trades: []
          };
          for (const item of fetchedData) {
            if (!item.id) continue;
            grouped.all.push(item);
            switch (item.appName) {
              case "Product":
                grouped.products.push(item);
                break;
              case "UserSiteProject":
                grouped.projects.push(item);
                break;
              case "Trade":
                grouped.trades.push(item);
                break;
              default:
                break;
            }
          }
          let mergedGrouped;
          if (hasExisting) {
            // Return visit: prepend new posts so latest appear first
            mergedGrouped = {
              all: [...grouped.all, ...communityPostIds.all],
              products: [...grouped.products, ...communityPostIds.products],
              projects: [...grouped.projects, ...communityPostIds.projects],
              trades: [...grouped.trades, ...communityPostIds.trades]
            };
          } else {
            // First visit: use freshly fetched grouped data
            mergedGrouped = grouped;
          }
          dispatch(setCommunityPostIds(mergedGrouped));
          setPosts([]);
          setCurrentBatchIndex(0);
          setHasMore(true);
          await fetchPostsBatch(0, mergedGrouped?.all, token);
        } else if (hasExisting) {
          // No new posts but we have existing cached IDs — show them
          setPosts([]);
          setCurrentBatchIndex(0);
          setHasMore(true);
          await fetchPostsBatch(0, communityPostIds.all, token);
        } else {
          setHasMore(false);
        }
      }
    } catch (error) {
      console.error("Error fetching post IDs: ", error);
      setHasMore(false);
    } finally {
      if (token === requestTokenRef.current) {
        setIsLoading(false);
      }
    }
  };

  // Fetch a batch of posts using post IDs
  const fetchPostsBatch = async (batchIndex, postIds = communityPostIds?.all, requestToken) => {
    if (!postIds || postIds.length === 0) {
      setHasMore(false);
      return;
    }
    try {
      setIsFetchingNextPage(true);
      const userId = loginData?.userInformation?.UserId;
      const accessToken = loginData?.accessToken;
      const startIndex = batchIndex * PAGE_SIZE;
      const endIndex = Math.min(startIndex + PAGE_SIZE, postIds.length);
      const batchItems = postIds.slice(startIndex, endIndex);
      const getPosts = await fetchPostsByIds(batchItems, userId, accessToken);
      if (requestToken !== undefined && requestToken !== requestTokenRef.current) return;
      if (getPosts.length === 0) {
        setHasMore(false);
        return;
      }
      if (batchIndex === 0) {
        setPosts(getPosts);
      } else {
        setPosts((prevPosts) => [...prevPosts, ...getPosts]);
      }
      if (endIndex >= postIds.length) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }
      setCurrentBatchIndex(batchIndex + 1);
      if (location?.state?.from === "from-notification") {
        let getPostId = location?.state?.item?.custom1;
        let getPostItem = getPosts.find((post) => post.id === getPostId);
        console.log("getPostItem", getPostItem);
        setSelectPost(getPostItem);
        setOpenComment(true);
      }
    } catch (error) {
      console.error("Error fetching posts batch: ", error);
      setHasMore(false);
    } finally {
      if (requestToken === undefined || requestToken === requestTokenRef.current) {
        setIsFetchingNextPage(false);
      }
    }
  };

  // Helper function to fetch post details by IDs
  const fetchPostsByIds = async (postIds, userId, accessToken) => {
    if (!postIds || postIds.length === 0) return [];
    let getPrimaryPosts = postIds;
    // Fetch site posts separately if any
    let getSitesposts = postIds.filter((post) => post.formatTypeId === 5);
    if (getSitesposts.length > 0) {
      const response = await Postrequestcall(
        GET_SITES_POSTS_BY_ID,
        { AppRecordIds: JSON.stringify(getSitesposts.map((post) => post.appRecordId)) },
        accessToken,
        false
      );
      if (response?.status === 200) {
        getSitesposts = response?.data?.data || [];
      }
    }
    // Fetch primary post details
    const obj = {
      PostIds: JSON.stringify(getPrimaryPosts.map((post) => post.id)),
      UserId: userId,
      AppLanguage: selectedLanguage || "en"
    };
    const response = await Postrequestcall(GET_POSTS_BY_ID, obj, accessToken, false);
    if (response?.status === 200 && response?.data?.data?.length > 0) {
      getPrimaryPosts = response.data.data;
    }
    // Merge site post data with primary posts
    getPrimaryPosts = getPrimaryPosts.map((post) => {
      if (post.formatTypeId === 5) {
        const getSitePost = getSitesposts.find((sitePost) => sitePost.id === post.appRecordId);
        return {
          ...post,
          site: getSitePost
        };
      }
      return post;
    });

    return getPrimaryPosts;
  };

  // Handle infinite scroll next page
  const handleFetchNextPage = async () => {
    if (isLoading || isFetchingNextPage || !hasMore) {
      return;
    }
    await getPostIdsByAppName(selectedTopMenu, currentBatchIndex, requestTokenRef.current);
  };

  const getPostIdsByAppName = async (index, currentBatchIndex, requestToken) => {
    switch (index) {
      case 0:
        await fetchPostsBatch(currentBatchIndex, communityPostIds.all, requestToken);
        break;
      case 1:
        await fetchPostsBatch(currentBatchIndex, communityPostIds.projects, requestToken);
        break;
      case 2:
        await fetchPostsBatch(currentBatchIndex, communityPostIds.trades, requestToken);
        break;
      case 3:
        await fetchPostsBatch(currentBatchIndex, communityPostIds.products, requestToken);
        break;
      default:
        break;
    }
  };

  // Handle top menu click — UI updates immediately, API call is debounced (300ms)
  const handleTopMenuClick = (index) => {
    if (index === selectedTopMenu) return;
    // Invalidate any in-flight requests from the previous tab
    requestTokenRef.current += 1;
    const token = requestTokenRef.current;
    // Immediately reflect tab change in UI
    setSelectedTopMenu(index);
    setPosts([]);
    setCurrentBatchIndex(0);
    setHasMore(false);
    setIsLoading(true);
    const scrollEl = document.getElementById("co_scrollList");
    if (scrollEl) scrollEl.scrollTop = 0;
    // Debounce the API call — cancel any pending request from a previous rapid click
    if (tabDebounceRef.current) clearTimeout(tabDebounceRef.current);
    tabDebounceRef.current = setTimeout(async () => {
      await getPostIdsByAppName(index, 0, token);
      setIsLoading(false);
    }, 1000);
  };

  const handleAddPostClick = (event) => {
    event.target.blur();
    dispatch(showAddPostModal(true));
  };

  const handleAddPostDialogClose = () => {
    dispatch(showAddPostModal(false));
  };

  const PostSkeleton = () => (
    <Card sx={{ p: 2, mb: 2 }}>
      <Stack direction="row" spacing={2} sx={{ alignItems: "center", mb: 2 }}>
        <Skeleton variant="circular" width={40} height={40} />
        <Box sx={{ width: "100%" }}>
          <Skeleton variant="text" width="60%" height={20} />
          <Skeleton variant="text" width="40%" height={20} />
        </Box>
      </Stack>
      <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 2 }} />
    </Card>
  );

  // Memoize posts rendering for smooth scroll performance
  const renderedPosts = useMemo(() => {
    return finalFeed?.map((postItem, index) => {
      return (
        <div
          ref={(el) => (postRefs.current[postItem.id] = el)}
          key={postItem?.id?.toString()}
          data-id={postItem?.id}
          style={{ willChange: "transform", transform: "translateZ(0)" }} // Hardware acceleration
        >
          {postItem.formatTypeId === 8 ? (
            <UserSuggestion data={postItem} />
          ) : postItem?.formatTypeId === "marketing-ad" || postItem?.formatTypeId === "cd-ad" ? (
            <Ads key={postItem?.adIndex || index} ad={postItem} />
          ) : (
            <Post from="community" postItem={postItem} setPosts={setPosts} posts={posts} />
          )}
        </div>
      );
    });
  }, [finalFeed]);

  return (
    <>
      <div
        style={{ height: "95vh", overflow: "scroll", scrollbarWidth: "none" }}
        id="co_scrollList">
        <Container sx={{ padding: "0px" }} maxWidth="md">
          <Box
            className="main-content-Box"
            sx={{
              paddingBottom: "16px",
              background: "#F2F2F2",
              height: "auto",
              scrollbarWidth: "none"
            }}>
            <Stack
              sx={{
                position: "absolute",
                maxWidth: {
                  xs: "100%", // mobile
                  sm: 480, // small screens
                  md: 736, // medium screens
                  lg: 852 // large screens
                },
                width: {
                  xs: "100%",
                  sm: "100%",
                  md: "100%",
                  lg: "100%"
                }
              }}>
              <Stack
                spacing={1.5}
                style={{
                  position: "sticky",
                  top: 8,
                  zIndex: 1,
                  backgroundColor: "#f2f2f2",
                  paddingBottom: 2.5,
                  paddingTop: "10px"
                }}>
                <Stack spacing={2} direction={"row"}>
                  {initTopMenu.map((menu, index) => {
                    const isSelected = index === selectedTopMenu;
                    return (
                      <Button
                        size="large"
                        key={index}
                        label={menu?.forTranlate}
                        variant={"contained"}
                        color={isSelected ? "primary" : "white"}
                        style={{
                          textTransform: "none",
                          borderRadius: "20px"
                        }}
                        onClick={() => handleTopMenuClick(index)}>
                        {menu}
                      </Button>
                    );
                  })}
                </Stack>
              </Stack>
            </Stack>
            <Card ref={textFieldRef} className="pagelist-content" style={{ marginTop: "60px" }}>
              <Stack sx={{ padding: 1, alignItems: "center" }} direction="row" spacing={1}>
                <Avatar src={loginData?.pictureUrl || ""} alt="profile-pic" />
                <TextField
                  size="small"
                  placeholder={MODAL_ADD_POST_PLACEHOLDER}
                  fullWidth
                  onClick={handleAddPostClick}
                />
              </Stack>
            </Card>
            <div
              className="main-content-post-card"
              style={{
                scrollbarWidth: "none",
                WebkitOverflowScrolling: "touch",
                willChange: "scroll-position",
                overscrollBehavior: "contain"
              }}>
              {isLoading || (isFetchingNextPage && posts.length === 0) ? (
                <Stack spacing={2}>
                  {[...Array(4)].map((_, idx) => (
                    <PostSkeleton key={idx} />
                  ))}
                </Stack>
              ) : posts?.length > 0 ? (
                <InfiniteScroll
                  scrollableTarget="co_scrollList"
                  dataLength={finalFeed?.length || 0}
                  next={handleFetchNextPage}
                  hasMore={hasMore}
                  style={{
                    scrollbarWidth: "none",
                    overflow: "visible"
                  }}
                  scrollThreshold={0.8}
                  loader={
                    isFetchingNextPage && (
                      <Grid container sx={{ justifyContent: "center" }} style={{ padding: "20px 0" }}>
                        <CircularProgress size={30} style={{ color: BIBlue }} />
                      </Grid>
                    )
                  }
                  endMessage={
                    posts?.length > 0 &&
                    !hasMore && (
                      <Typography sx={{ textAlign: "center", color: "text.secondary" }} variant="body2" style={{ padding: "20px 0" }}>
                        {TEXT_ALL_POST_SEEN}
                      </Typography>
                    )
                  }>
                  <Stack
                    spacing={1}
                    sx={{
                      backgroundColor: "#f2f2f2",
                      // Enable GPU acceleration for smooth scrolling
                      transform: "translateZ(0)",
                      backfaceVisibility: "hidden",
                      perspective: 1000
                    }}>
                    {renderedPosts}
                  </Stack>
                </InfiniteScroll>
              ) : !isLoading && posts?.length === 0 ? (
                <Box className="empty-data-card pagelist-content">
                  <Emptydata
                    icon={<Message className="emptydata-icon" />}
                    firstText={TEXT_PLACEHOLDER_NO_POSTS}
                  />
                </Box>
              ) : null}
            </div>
          </Box>
          {!isTextFieldVisible && (
            <AddButton toolTipText={BUTTON_ADD_POST} onAddClick={handleAddPostClick} />
          )}
        </Container>

        {showAddpost && (
          <AddPostDialog
            appRecordId={0}
            appId={25}
            open={showAddpost}
            onClose={() => handleAddPostDialogClose()}
            updatePosts={() => {
              // Reset posts and refetch
              setPosts([]);
              setCurrentBatchIndex(0);
              setHasMore(true);
              fetchPostIds();
            }}
            messageTitle={MODAL_TEXT_INFO}
          />
        )}

        {/* It will open the comment modal from background notification click * */}
        {openComment && (
          <PostComment
            open={openComment}
            postItem={selectPost}
            setSelectPost={setSelectPost}
            posts={posts}
            onClose={() => setOpenComment(false)}
            setPosts={setPosts}
          />
        )}
      </div>
    </>
  );
}
